import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Form, Field } from 'react-final-form';
import { Box, Flex } from 'rebass';
import { useStore } from 'effector-react';
import { Trans, t } from '@lingui/macro';

import { Button, Dialog, Menu } from '@oca/ui';
import { ArrowDropDown } from '@oca/icons';
import { TextFieldWrapper } from '@lib/final-form-oca-ui';
import { i18n } from '@lib/i18n';

import {
  $workspaces,
  $currentWorkspace,
  $currentWorkspaceId,
  workspaceSwitched,
  createWorkspaceFx,
} from '../model/session';
import { errorMessage } from '../lib/format';

/** Header control that lists the user's RecordFlow workspaces and adds new ones. */
export function WorkspaceSwitcher() {
  const workspaces = useStore($workspaces);
  const current = useStore($currentWorkspace);
  const currentId = useStore($currentWorkspaceId);
  const [isCreateOpen, setCreateOpen] = React.useState(false);

  return (
    <React.Fragment>
      <Menu
        placement="bottom-end"
        anchor={
          <Trigger type="button" aria-label={i18n._(t`Switch workspace`)}>
            <Tile>{(current ? current.name : '?').charAt(0)}</Tile>
            <TriggerName>
              {current ? current.name : <Trans>No workspace</Trans>}
            </TriggerName>
            <ArrowDropDown />
          </Trigger>
        }
      >
        {workspaces.map(workspace => (
          <Menu.Item
            key={workspace.id}
            aria-current={workspace.id === currentId ? 'true' : undefined}
            onClick={() => workspaceSwitched(workspace.id)}
            fontWeight={workspace.id === currentId ? 600 : 400}
          >
            <Tile>{workspace.name.charAt(0)}</Tile>
            <ItemName>{workspace.name}</ItemName>
          </Menu.Item>
        ))}
        <Menu.Item onClick={() => setCreateOpen(true)} color="primary.main">
          + <Trans>Create workspace</Trans>
        </Menu.Item>
      </Menu>
      {isCreateOpen && (
        <CreateWorkspaceDialog onClose={() => setCreateOpen(false)} />
      )}
    </React.Fragment>
  );
}

/** Also used by the main page when the account has no workspace yet. */
export function CreateWorkspaceDialog({ onClose }) {
  async function submit({ name }) {
    try {
      await createWorkspaceFx(name.trim());
      if (onClose) onClose();
      return null;
    } catch (error) {
      return { name: errorMessage(error) };
    }
  }

  return (
    <Dialog title={i18n._(t`Create workspace`)} onClose={onClose}>
      <Box style={{ minWidth: 360 }}>
        <Form
          onSubmit={submit}
          validate={({ name }) =>
            name && name.trim()
              ? {}
              : { name: i18n._(t`Enter a workspace name`) }
          }
          render={({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit}>
              <Box mb={20}>
                <Field
                  name="name"
                  label={<Trans>Name</Trans>}
                  component={TextFieldWrapper}
                  autoFocus
                  required
                  spaceAfter
                />
              </Box>
              <Flex justifyContent="flex-end" style={{ gap: '10px' }}>
                {onClose && (
                  <Button
                    variant="secondary"
                    onClick={onClose}
                    disabled={submitting}
                  >
                    <Trans>Cancel</Trans>
                  </Button>
                )}
                <Button variant="primary" type="submit" disabled={submitting}>
                  <Trans>Create workspace</Trans>
                </Button>
              </Flex>
            </form>
          )}
        />
      </Box>
    </Dialog>
  );
}

CreateWorkspaceDialog.propTypes = {
  // eslint-disable-next-line react/require-default-props
  onClose: PropTypes.func,
};

const Trigger = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 8px 0 6px;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  background: #ffffff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #181d27;

  &:hover,
  &:focus-visible {
    border-color: #d8b4fe;
    outline: none;
  }

  svg {
    flex-shrink: 0;
  }
`;

const Tile = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  margin-right: 8px;
  border-radius: 7px;
  background: rgba(123, 68, 244, 0.1);
  color: #7b44f4;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;

  ${Trigger} & {
    margin-right: 0;
  }
`;

const TriggerName = styled.span`
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ItemName = styled.span`
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
